import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { setOrgContext, organizationsAPI } from '../services/api';
import { conReintentoDeRed } from '../utils/reintentoDeRed';
import { guardarOrgElegida, leerOrgElegida, resolverOrgGuardada } from '../utils/orgElegidaGuardada';
import { decidirOrganizacion, debePersistirEleccion } from '../utils/eleccionDeOrganizacion';
import { TERMS_VERSION } from '../config/legal';
import { contextoDeRegistro, completeRegistration, leerFbp, leerFbc } from '../utils/metaPixel';
import { signUp, metodoDeAcceso } from '../utils/googleTag';
import { getFbclid } from '../utils/attribution';

const OrganizationContext = createContext();

const emptyContext = {
  mode: 'organization',
  isOrganization: false,
  orgId: null,
  organization: null,
  member: null,
  permissions: {}
};

/**
 * Organization Context Provider
 *
 * Manages organization state. All users operate within an organization.
 */
export function OrganizationProvider({ children }) {
  const [user, setUser] = useState(null);
  const [context, setContext] = useState(emptyContext);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsConsent, setNeedsConsent] = useState(false);
  // true mientras el usuario actual NUNCA haya aceptado los términos (ver loadOrgContext).
  const primeraAceptacionRef = useRef(false);
  // orgsResolved: true only once the org membership for the CURRENT user has been
  // definitively resolved into context (a 2xx list response processed). The gate
  // waits on THIS, not on `loading` — see loadOrgContext. orgError: resolution
  // failed after retries → error screen instead of an infinite loader.
  const [orgsResolved, setOrgsResolved] = useState(false);
  const [orgError, setOrgError] = useState(false);
  // eleccionPendiente: pertenece a varias organizaciones y no hay ninguna elección válida
  // que aplicar. Lleva la lista YA ORDENADA (no el orden de la consulta) y si lo que había
  // guardado era inválido, que es lo que decide el aviso de la pantalla y la limpieza.
  const [eleccionPendiente, setEleccionPendiente] = useState(null);

  // Resolve the org context for a user. Extracted so the error screen can re-run it.
  const loadOrgContext = useCallback(async (u) => {
    // Reset the resolution signals at the START of every pass so the onboarding
    // gate (which waits on orgsResolved) never evaluates on a stale/partial
    // context. This is what closes the "loading:false + emptyContext" window that
    // bounced users (incl. those WITH an org) to /contratar.
    setOrgsResolved(false);
    setOrgError(false);
    setEleccionPendiente(null);
    setLoading(true);

    try {
      const clientRef = doc(db, 'clientes', u.uid);
      const clientSnap = await getDoc(clientRef);
      const clientData = clientSnap.data() || {};

      // Legal consent gate: version-aware (a future bump re-triggers acceptance).
      setNeedsConsent(!clientData?.termsAcceptedAt || clientData.termsVersion !== TERMS_VERSION);
      // ¿Es la PRIMERA aceptación? Es lo que distingue un registro nuevo de un cambio de
      // versión legal, y lo que `acceptConsent` usa para el `CompleteRegistration` de Meta.
      primeraAceptacionRef.current = !clientData?.termsAcceptedAt;

      // Fetch org membership with bounded retries. Key distinction:
      //   - a THROW (401 token-not-ready / network) is a FAILURE → retry, and if it
      //     persists → error screen. NEVER fabricate emptyContext from a failure.
      //   - a 2xx response (even with zero orgs) is SUCCESS → genuine no-org (a
      //     legit new user); resolves normally, NOT via the retry path.
      //   - un FALLO DE RED (nadie contestó) → se reintenta con paciencia: el
      //     servidor vive en una Cloud Run de UNA instancia y su reemplazo deja una
      //     ventana de varios segundos en la que las peticiones se rechazan ANTES del
      //     contenedor, sin headers CORS. Ver `utils/reintentoDeRed.js`.
      //   - un ERROR HTTP (401, 403, 500…) → NO se reintenta: el servidor ya dio su
      //     veredicto y repetirlo sólo retrasa la pantalla de error diez segundos.
      //
      // Mientras reintenta, `orgsResolved` sigue en false y el usuario ve la pantalla
      // de «inicializando» de `App.js:127`; la de error sólo aparece al agotarse el
      // presupuesto, que es el contrato que esta función ya tenía.
      let response = null;
      try {
        response = await conReintentoDeRed(() => organizationsAPI.list(), {
          alReintentar: (intento, total, err) =>
            console.warn(`[OrganizationContext] fallo de red al listar organizaciones (intento ${intento}/${total}), reintentando:`, err.message),
        });
      } catch (err) {
        console.error('[OrganizationContext] no se pudo listar organizaciones:', err.message);
        setOrgError(true);
        setLoading(false);
        return;
      }

      let userOrgs = [];
      if (response && response.ok && Array.isArray(response.organizations)) {
        userOrgs = response.organizations.map((org) => ({
          id: org.id,
          ...org,
          membership: {
            role: org.membership?.role,
            permissions: org.membership?.permissions || {},
            joinedAt: org.membership?.joinedAt
          }
        }));
      }
      setOrganizations(userOrgs);

      // A qué organización entra. La decisión vive en `utils/eleccionDeOrganizacion.js`
      // —función pura, con tests— porque ANTES era una cadena de tres `else if` aquí
      // dentro, imposible de ejercitar sin montar Firestore, y el último eslabón era
      // `userOrgs[0]`: un «primero» que no significa nada (el orden de la consulta de
      // pertenencias del servidor) y que además SOBRESCRIBÍA en silencio una elección
      // guardada que ya no valiera.
      let pendingOrgId = null;
      try {
        pendingOrgId = localStorage.getItem('selectedOrganizationId');
      } catch { /* modo privado: sin invitación pendiente, no es un fallo */ }

      const savedContext = clientData.organizationContext || {};
      const savedOrgId = resolverOrgGuardada({
        firestoreOrgId: savedContext.currentOrgId ?? null,
        localOrgId: leerOrgElegida(u.uid),
      });

      const decision = decidirOrganizacion({ orgs: userOrgs, pendingOrgId, savedOrgId });

      if (decision.accion === 'entrar' && decision.motivo === 'invitacion') {
        try { localStorage.removeItem('selectedOrganizationId'); } catch { /* ídem */ }
      }

      // «Elegir» resuelve la sesión IGUAL DE DEFINITIVAMENTE que «entrar»: `orgsResolved`
      // pasa a true y el loader se levanta. Si se dejara en false, `App.js:127` mostraría
      // «inicializando» para siempre. Lo que distingue el caso es `needsOrgChoice`, y la
      // puerta de onboarding tiene que excluirlo — sin org resuelta, `needsOnboarding` es
      // true y `App.js` mandaría a /contratar en vez de enseñar la pantalla.
      if (decision.accion === 'elegir') {
        if (decision.guardadaInvalida) {
          // ANCLA ESTABLE. Mide algo que antes era invisible: cuánta gente tenía guardada
          // una organización de la que ya no es miembro. Sin ids completos ni correo.
          console.warn('orgs:eleccion-guardada-invalida', {
            orgs: decision.orgs.length,
            guardada: String(savedOrgId).slice(0, 8),
          });
        }
        setEleccionPendiente({
          orgs: decision.orgs,
          guardadaInvalida: !!decision.guardadaInvalida,
        });
      } else {
        setEleccionPendiente(null);
      }

      const targetOrgId = decision.accion === 'entrar' ? decision.orgId : null;

      let resolvedContext = emptyContext;
      if (targetOrgId) {
        const org = userOrgs.find((o) => o.id === targetOrgId);
        resolvedContext = {
          mode: 'organization',
          isOrganization: true,
          orgId: targetOrgId,
          organization: org,
          member: org.membership,
          permissions: org.membership.permissions
        };
      }

      // Pending invitation → hand off to the accept flow BEFORE unblocking the gate.
      // If we set orgsResolved first, there'd be a window where the gate is unblocked
      // AND hasPendingInvite() is already false (tokens just removed) → race between
      // <Navigate to="/contratar"> and this navigation. Keeping orgsResolved false
      // leaves the loader up until the browser navigates. (On the reload at
      // /accept-invitation/*, the route exclusion protects the invitee.)
      const pendingInviteToken = localStorage.getItem('pendingInviteToken');
      const pendingInviteUrl = localStorage.getItem('pendingInviteUrl');
      if (pendingInviteToken && pendingInviteUrl) {
        localStorage.removeItem('pendingInviteToken');
        localStorage.removeItem('pendingInviteUrl');
        window.location.href = pendingInviteUrl;
        return; // full navigation underway — do NOT unblock the gate
      }

      // Commit the DEFINITIVE context and mark resolved together — no await in
      // between, so no intermediate render can expose emptyContext as "resolved".
      setContext(resolvedContext);
      setOrgContext(resolvedContext);
      setOrgsResolved(true);
      setLoading(false);

      // Persist the chosen org for future sessions — fire-and-forget, AFTER the
      // resolution is committed (previously awaited between setContext and the flag).
      //
      // SÓLO SI CAMBIA, y eso cierra una carrera medida en producción el 17-sep-2026: esta
      // escritura no se espera, así que la de una carga anterior puede aterrizar encima de
      // una posterior y correcta. Ocurrió dos veces seguidas — al aceptar una invitación, la
      // organización activa volvía sola a la de antes, sin error y sin log:
      //
      //   15:38:15  la página lista organizaciones → 1 (aún sin aceptar); decide «única»
      //   15:38:18  el servidor acepta la invitación → escribe Brokers
      //   15:38:3x  aterriza la escritura de las 15:38:15 → PUX App PISA a Brokers
      //
      // `savedContext` es lo leído en ESTA carga (`:61-62`, `getDoc` al entrar), no un valor
      // de módulo: comparar contra él es comparar contra lo que había cuando empezamos.
      //
      // El criterio es el MISMO para los dos sitios. El respaldo local existe para cuando la
      // escritura de Firestore no ocurre —`updateDoc` LANZA si el documento no está y el
      // `.catch` se lo traga—, y si no hay nada que escribir tampoco hay nada que respaldar.
      if (debePersistirEleccion({ targetOrgId, savedContext })) {
        guardarOrgElegida(u.uid, targetOrgId);
        updateDoc(clientRef, {
          organizationContext: { mode: 'organization', currentOrgId: targetOrgId }
        }).catch((err) => console.error('Error saving org context to Firestore:', err));
      }
    } catch (error) {
      // getDoc or unexpected failure → retryable error screen, NOT emptyContext.
      console.error('Error loading organization context:', error);
      setOrgError(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setContext(emptyContext);
        setOrganizations([]);
        setNeedsConsent(false);
        setOrgsResolved(false);
        setOrgError(false);
        setEleccionPendiente(null);
        setLoading(false);
        return;
      }
      setUser(u);
      await loadOrgContext(u);
    });

    return () => unsub();
  }, [loadOrgContext]);

  // Retry hook for the error screen.
  const retryOrgResolution = useCallback(() => {
    if (auth.currentUser) loadOrgContext(auth.currentUser);
  }, [loadOrgContext]);

  /**
   * Switch to a different organization
   */
  const switchContext = async (mode, orgId = null, orgData = null) => {
    if (!user) {
      console.warn('Cannot switch context without authenticated user');
      return;
    }

    try {
      if (orgId) {
        const org = orgData || organizations.find(o => o.id === orgId);

        if (!org) {
          console.error('Organization not found:', orgId);
          return;
        }

        const newContext = {
          mode: 'organization',
          isOrganization: true,
          orgId: orgId,
          organization: org,
          member: org.membership,
          permissions: org.membership.permissions
        };

        setContext(newContext);
        setOrgContext(newContext);

        // La elección se guarda SIEMPRE aquí, y va ANTES de Firestore a propósito: la
        // escritura de abajo puede fallar (o no ocurrir siquiera, ver el `snap.exists()`),
        // y lo que no se puede perder es la preferencia del usuario.
        //
        // POR QUÉ HAY DOS SITIOS, corregido el 17-sep-2026: NO es que el invitado carezca de
        // `clientes/{uid}` —el aceptar-invitación se lo crea con un `set(..., {merge:true})`,
        // `organizationRoutes.js:1160-1170`—. Es que la escritura de Firestore puede no
        // ocurrir: el `updateDoc` de abajo está tras un `snap.exists()` y, en `loadOrgContext`,
        // LANZA si el documento no está. Esto es la red de seguridad de ese hueco, no la
        // fuente. La clave lleva el uid, así que dos cuentas del mismo navegador no se mezclan.
        guardarOrgElegida(user.uid, orgId);

        // Save to Firestore
        const clientRef = doc(db, 'clientes', user.uid);
        const snap = await getDoc(clientRef);
        if (snap.exists()) {
          await updateDoc(clientRef, {
            'organizationContext.mode': 'organization',
            'organizationContext.currentOrgId': orgId
          });
        }

        console.log('Switched to organization:', orgId);
      }
    } catch (error) {
      console.error('Error switching context:', error);
    }
  };

  /**
   * Lo que hace la pantalla de elección al pulsar una organización.
   *
   * LA LIMPIEZA DEL VALOR INVÁLIDO VA AQUÍ, DESPUÉS DE ELEGIR, Y NUNCA ANTES. Limpiar al
   * detectarlo convertiría un fallo transitorio de la lista —un 200 con menos
   * organizaciones de las que hay— en la pérdida definitiva de la preferencia. Mientras el
   * usuario no elige, el valor malo es inofensivo: ya no se aplica, sólo se conserva.
   *
   * `switchContext` escribe la elección nueva en los dos sitios, así que en el camino feliz
   * la limpieza ES esa sobrescritura. La comprobación de abajo cubre el camino que no lo es:
   * si el `updateDoc` falló, Firestore conserva el id inválido, y como Firestore MANDA sobre
   * localStorage el siguiente login volvería a preguntar — en bucle, cada vez. Por eso se
   * lee de vuelta en lugar de suponer que la escritura ocurrió.
   */
  const elegirOrganizacion = async (orgId) => {
    await switchContext('organization', orgId);

    if (eleccionPendiente?.guardadaInvalida && user) {
      try {
        const clientRef = doc(db, 'clientes', user.uid);
        const snap = await getDoc(clientRef);
        const quedaEnFirestore = snap.exists() ? snap.data()?.organizationContext?.currentOrgId : null;
        if (quedaEnFirestore && quedaEnFirestore !== orgId) {
          await updateDoc(clientRef, {
            'organizationContext.mode': 'organization',
            'organizationContext.currentOrgId': orgId
          });
          console.warn('orgs:eleccion-guardada-invalida-limpiada');
        }
      } catch (err) {
        // No bloquea la entrada: el usuario ya está dentro de la organización que eligió.
        console.error('Error limpiando la elección inválida:', err.message);
      }
    }

    setEleccionPendiente(null);
  };

  /**
   * Persist legal consent for the current user and lift the gate.
   * Writes only non-billing fields, so it passes the existing clientes/{uid}
   * rules (create for a brand-new user, update/merge for an existing one).
   *
   * `CompleteRegistration` DE META SALE DE AQUÍ. La primera aceptación de los términos es el
   * «registro completo» del embudo comercial: es el primer acto deliberado dentro de la app,
   * ocurre una sola vez por cuenta, y cubre los dos caminos de alta (código por correo y
   * Google) sin depender de cuál fue. Ver `utils/metaPixel.js` → `contextoDeRegistro`.
   *
   * La escritura lleva `registroMeta` —contexto y cookies del pixel— porque el trigger de
   * servidor (`functions/triggers/metaRegistroCapi.js`) escucha ESTA MISMA escritura y manda
   * su mitad con el mismo `event_id` (el uid); sin el contexto no sabría si es un alta
   * comercial o un invitado. Sólo se escribe en la primera aceptación: un cambio de versión
   * legal no es un registro y no debe parecerlo.
   */
  const acceptConsent = async () => {
    if (!user) {
      console.warn('Cannot accept consent without authenticated user');
      return;
    }
    const esPrimera = primeraAceptacionRef.current === true;
    const contexto = contextoDeRegistro({
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      tienePendingInvite: (() => { try { return !!localStorage.getItem('pendingInviteToken'); } catch { return false; } })(),
    });
    const clientRef = doc(db, 'clientes', user.uid);
    await setDoc(
      clientRef,
      {
        termsAcceptedAt: serverTimestamp(),
        termsVersion: TERMS_VERSION,
        email: user.email || null,
        ...(esPrimera ? {
          registroMeta: {
            contexto,
            ...(contexto === 'alta' && leerFbp() ? { fbp: leerFbp() } : {}),
            ...(contexto === 'alta' && leerFbc(getFbclid()) ? { fbc: leerFbc(getFbclid()) } : {}),
          },
        } : {}),
      },
      { merge: true }
    );
    if (esPrimera) completeRegistration({ uid: user.uid, contexto });
    // GA4 `sign_up` · el mismo hecho que `CompleteRegistration`, sólo en el embudo comercial.
    if (esPrimera && contexto === 'alta') signUp({ method: metodoDeAcceso(user) });
    primeraAceptacionRef.current = false;
    setNeedsConsent(false);
  };

  /**
   * Refresh organizations list (useful after accepting an invitation)
   * Returns the updated organizations array
   */
  const refreshOrganizations = async () => {
    if (!user) return [];

    try {
      // Mismo servidor y misma ventana de reemplazo que el arranque: mismo presupuesto.
      const response = await conReintentoDeRed(() => organizationsAPI.list());

      if (response.ok && response.organizations) {
        const userOrgs = response.organizations.map(org => ({
          id: org.id,
          ...org,
          membership: {
            role: org.membership?.role,
            permissions: org.membership?.permissions || {},
            joinedAt: org.membership?.joinedAt
          }
        }));

        setOrganizations(userOrgs);
        console.log('Organizations refreshed via API:', userOrgs.length);
        return userOrgs;
      } else {
        console.warn('No organizations found or API returned error');
        setOrganizations([]);
        return [];
      }
    } catch (error) {
      console.error('Error refreshing organizations:', error);
      setOrganizations([]);
      return [];
    }
  };

  const value = {
    user,
    context,
    organizations,
    loading,
    needsConsent,
    acceptConsent,
    // Onboarding signal (Bloque 5): keyed on resolved org MEMBERSHIP, not
    // clientes.organizationId (owner-centric — an invited member has no
    // organizationId and would be trapped in the gate forever).
    //
    // `&& !eleccionPendiente` NO es una cautela: sin él, quien tiene DOS organizaciones y
    // ninguna elegida se lee como «sin organización» y `App.js` lo manda a /contratar. La
    // pantalla de elección no se vería nunca.
    needsOnboarding: !context.isOrganization && !eleccionPendiente,
    // Pertenece a varias y no hay elección válida que aplicar. Lo consume `App.js`, en la
    // misma ranura que ConsentGate: una pantalla EN LUGAR de `<Routes>`, así que no monta
    // ninguno de los 50 componentes que dan por hecha una organización resuelta.
    needsOrgChoice: !!eleccionPendiente,
    orgsParaElegir: eleccionPendiente?.orgs || [],
    eleccionGuardadaInvalida: !!eleccionPendiente?.guardadaInvalida,
    elegirOrganizacion,
    orgsResolved,
    orgError,
    retryOrgResolution,
    switchContext,
    refreshOrganizations
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

/**
 * Hook to use organization context
 */
export function useOrganization() {
  const context = useContext(OrganizationContext);

  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }

  return context;
}
